import type {
  AdminSkillQuery,
  BackfillCoverageInput,
  CreatePromptTemplateInput,
  CreateSkillInput,
  DraftPromptTemplateInput,
  GenerateQuestionsInput,
  ItemModelInput,
  QuestionDifficulty,
  QuestionStatus,
  QuestionType,
  ResetContentInput,
  SimGraduationLoopInput,
  SimRoadmapInput,
  UpdateDraftQuestionInput,
  UpdateSkillInput,
} from '@prepzio/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  adminApi,
  type LaunchWaiverInput,
  type QuestionLifecycle,
  type ReviewDecision,
} from '../lib/api';
import { adminKeys } from '../lib/query';

/**
 * Server-state hooks for the content pipeline (skills, coverage gaps, prompt
 * templates, drafts). Screens use these instead of hand-rolled fetch/useEffect;
 * mutations invalidate the affected query keys so lists stay consistent.
 */

/**
 * PRE-155: a page of skills, searched server-side.
 *
 * `placeholderData` keeps the previous page on screen while the next one loads,
 * so typing in the search box does not blank the table on every keystroke.
 */
export function useSkills(query: AdminSkillQuery = {}) {
  return useQuery({
    queryKey: adminKeys.skills(query),
    queryFn: () => adminApi.listSkills(query),
    placeholderData: (prev) => prev,
  });
}

/** Skill manager mutations — each invalidates the skills list (+ coverage gaps,
 * which are computed per skill × difficulty). */
export function useCreateSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSkillInput) => adminApi.createSkill(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.skillsAll() });
      void qc.invalidateQueries({ queryKey: adminKeys.coverageGapsRoot() });
    },
  });
}

export function useUpdateSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateSkillInput }) =>
      adminApi.updateSkill(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.skillsAll() });
      void qc.invalidateQueries({ queryKey: adminKeys.coverageGapsRoot() });
    },
  });
}

export function useDeleteSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminApi.deleteSkill(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.skillsAll() });
      void qc.invalidateQueries({ queryKey: adminKeys.coverageGapsRoot() });
    },
  });
}

export function useCoverageGaps(params: { limit?: number; offset?: number } = {}) {
  return useQuery({
    queryKey: adminKeys.coverageGaps(params),
    queryFn: () => adminApi.listCoverageGaps(params),
    // PRE-285: keep the previous page on screen while the next one loads, so
    // paging does not blank the list and bounce the scroll position.
    placeholderData: (prev) => prev,
  });
}

/**
 * PRE-261: bank health. A read, and only a read — asking for the report must
 * never change the bank, which is why there is a separate mutation below.
 */
/**
 * PRE-293: item models. Listing and previewing are free; only generation is not.
 *
 * The preview is a mutation rather than a query on purpose — it is keyed by what
 * is currently typed in the form, not by anything the server holds, and caching
 * it under a key would mean showing an expert the expansion of a model they have
 * since edited.
 */
export function useItemModels() {
  return useQuery({ queryKey: adminKeys.itemModels(), queryFn: () => adminApi.listItemModels() });
}

export function usePreviewItemModel() {
  return useMutation({ mutationFn: (input: ItemModelInput) => adminApi.previewItemModel(input) });
}

export function useCreateItemModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ItemModelInput) => adminApi.createItemModel(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.itemModels() }),
  });
}

export function useGenerateFromItemModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; count: number }) => adminApi.generateFromItemModel(v.id, v.count),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: adminKeys.itemModels() }),
        qc.invalidateQueries({ queryKey: DRAFTS_ROOT }),
        qc.invalidateQueries({ queryKey: adminKeys.coverageGapsRoot() }),
      ]),
  });
}

export function useBankHealth() {
  return useQuery({
    queryKey: adminKeys.bankHealth(),
    queryFn: () => adminApi.getBankHealth(),
  });
}

/**
 * PRE-283: the authoring pipeline as one line. A read, and only a read — the
 * decisions it reports on stay on the screens that own them.
 */
export function useQuestionFlow() {
  return useQuery({
    queryKey: adminKeys.questionFlow(),
    queryFn: () => adminApi.getQuestionFlow(),
  });
}

/**
 * PRE-261: the human half. Moving an item along the retirement flow is the one
 * thing the report deliberately cannot do for itself.
 */
export function useSetQuestionLifecycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; status: QuestionLifecycle; note?: string }) =>
      adminApi.setQuestionLifecycle(v.id, v.status, v.note),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.bankHealth() });
      // Retiring an item changes what the bank can serve, so the gap report is
      // stale the moment this succeeds.
      void qc.invalidateQueries({ queryKey: adminKeys.coverageGapsRoot() });
    },
  });
}

/** Which model the authoring tier runs — low-churn, cache long. */
export function useLlmInfo() {
  return useQuery({
    queryKey: adminKeys.llmInfo(),
    queryFn: () => adminApi.getLlmInfo(),
    staleTime: 5 * 60_000,
  });
}

/** Per-(skill × status) content counts for the Content overview. */
export function useContentSummary() {
  return useQuery({
    queryKey: adminKeys.contentSummary(),
    queryFn: () => adminApi.getContentSummary(),
  });
}

/** Roadmaps for the explorer picker. */
export function useRoadmaps() {
  return useQuery({ queryKey: adminKeys.roadmaps(), queryFn: () => adminApi.listRoadmaps() });
}

/** LLM cost dashboard, aggregated live from the llm_calls ledger. */
export function useCosts() {
  return useQuery({
    queryKey: adminKeys.costs(),
    queryFn: () => adminApi.getCosts(),
    staleTime: 60_000,
  });
}

export function useSystemHealth() {
  return useQuery({
    queryKey: adminKeys.systemHealth(),
    queryFn: () => adminApi.getSystemHealth(),
    staleTime: 30_000,
  });
}

/** Runtime config / feature flags (registry + KV overrides). */
export function useAppConfig() {
  return useQuery({ queryKey: adminKeys.appConfig(), queryFn: () => adminApi.listConfig() });
}

export function useUpdateConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      adminApi.updateConfig(key, value),
    onSuccess: () => void qc.invalidateQueries({ queryKey: adminKeys.appConfig() }),
  });
}

/** Real learners for the Users explorer list. */
export function useUsers() {
  return useQuery({ queryKey: adminKeys.users(), queryFn: () => adminApi.listUsers() });
}

/** Full detail (profile · sessions · roadmaps) for one user. */
export function useUserDetail(id: string | null) {
  return useQuery({
    queryKey: adminKeys.userDetail(id ?? ''),
    queryFn: () => adminApi.getUserDetail(id!),
    enabled: !!id,
  });
}

/** BR-85: erase a user's learner data (keeps sign-in). Invalidates their detail + the list. */
export function useResetUserData(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (confirmEmail: string) => adminApi.resetUserData(id, confirmEmail),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.userDetail(id) });
      void qc.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

/** Real users (with an onboarding profile) for the simulation persona picker. */
export function useSimUsers() {
  return useQuery({ queryKey: adminKeys.simUsers(), queryFn: () => adminApi.listSimUsers() });
}

/**
 * Run the full simulated pipeline (persona → assessment → roadmap). On success a
 * new simulation roadmap exists, so refresh the explorer list.
 */
export function useSimulateRoadmap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SimRoadmapInput) => adminApi.simulateRoadmap(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.roadmaps() }),
  });
}

/** PRE-198: run the graduation → re-loop simulation over a sim roadmap. */
export function useSimulateGraduationLoop() {
  return useMutation({
    mutationFn: (input: SimGraduationLoopInput) => adminApi.simulateGraduationLoop(input),
  });
}

/** Delete a simulation (cascade) and refresh the explorer list. */
export function useDeleteSimRoadmap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roadmapId: string) => adminApi.deleteSimRoadmap(roadmapId),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.roadmaps() }),
  });
}

/** Full roadmap structure (→ Mermaid). Only fetches when an id is selected. */
export function useRoadmapDetail(id: string | null) {
  return useQuery({
    queryKey: adminKeys.roadmapDetail(id ?? ''),
    queryFn: () => adminApi.getRoadmapDetail(id!),
    enabled: !!id,
  });
}

/** Full detail of a single question (roadmap explorer inspector). */
export function useAdminQuestion(id: string | null) {
  return useQuery({
    queryKey: adminKeys.question(id ?? ''),
    queryFn: () => adminApi.getQuestion(id!),
    enabled: !!id,
    staleTime: 5 * 60_000,
  });
}

export function usePromptTemplates(name?: string) {
  return useQuery({
    queryKey: adminKeys.promptTemplates(name),
    queryFn: () => adminApi.listTemplates(name),
  });
}

export function useDrafts(params: {
  status: QuestionStatus;
  skillId?: string;
  type?: QuestionType;
  difficulty?: QuestionDifficulty;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: adminKeys.drafts(params),
    queryFn: () => adminApi.listDrafts(params),
    placeholderData: (prev) => prev,
  });
}

/** PRE-262: the numbers that say whether the judge threshold is set right. */
export function useReviewStats() {
  return useQuery({
    queryKey: adminKeys.reviewStats(),
    queryFn: () => adminApi.getReviewStats(),
  });
}

/**
 * PRE-262: apply a whole sitting at once.
 *
 * Partial success is expected, not exceptional — two admins on one queue is the
 * normal case — so the caller gets the per-decision results rather than a throw.
 */
export function useReviewBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (decisions: ReviewDecision[]) => adminApi.reviewBatch(decisions),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: DRAFTS_ROOT }),
        qc.invalidateQueries({ queryKey: adminKeys.reviewStats() }),
        qc.invalidateQueries({ queryKey: adminKeys.coverageGapsRoot() }),
      ]),
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

const TEMPLATES_ROOT = ['admin', 'prompt-templates'] as const;
const DRAFTS_ROOT = ['admin', 'drafts'] as const;

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePromptTemplateInput) => adminApi.createTemplate(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: TEMPLATES_ROOT }),
  });
}

/**
 * AI-assisted prompt drafting: brief → full template body. Read-only (drafts
 * text; the operator still saves it via useCreateTemplate), so no invalidation.
 */
export function useDraftPromptTemplate() {
  return useMutation({
    mutationFn: (input: DraftPromptTemplateInput) => adminApi.draftPromptTemplate(input),
  });
}

/**
 * BR-81: discard drafts (PRE-296).
 *
 * Invalidates the coverage gaps as well as the lists, because discarding is
 * what makes a cell short again — a screen still claiming the cell is filled
 * would send the next generation somewhere else.
 */
export function useResetContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ResetContentInput) => adminApi.resetContent(input),
    onSuccess: (_res, input) =>
      input.dryRun
        ? undefined
        : Promise.all([
            qc.invalidateQueries({ queryKey: DRAFTS_ROOT }),
            qc.invalidateQueries({ queryKey: adminKeys.reviewStats() }),
            qc.invalidateQueries({ queryKey: adminKeys.coverageGapsRoot() }),
          ]),
  });
}

export function useActivateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminApi.activateTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: TEMPLATES_ROOT }),
  });
}

export function useGenerate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GenerateQuestionsInput) => adminApi.generate(input),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: DRAFTS_ROOT }),
        qc.invalidateQueries({ queryKey: adminKeys.coverageGapsRoot() }),
      ]),
  });
}

/**
 * PRE-284: generate for one skill — every below-floor cell, at the floor's count.
 *
 * A queue producer, so nothing to show but the plan; the drafts arrive in the
 * review queue asynchronously. The coverage worklist is invalidated because the
 * cells are now spoken for, and the drafts list because it is about to change
 * under the operator without them doing anything.
 */
export function useBackfillSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BackfillCoverageInput) => adminApi.backfillSkill(input),
    onSuccess: (_data, input) => {
      if (input.dryRun) return;
      return Promise.all([
        qc.invalidateQueries({ queryKey: DRAFTS_ROOT }),
        qc.invalidateQueries({ queryKey: adminKeys.coverageGapsRoot() }),
      ]);
    },
  });
}

export function useUpdateDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: UpdateDraftQuestionInput }) =>
      adminApi.updateDraft(vars.id, vars.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: DRAFTS_ROOT }),
  });
}

export function useApproveDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminApi.approve(id),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: DRAFTS_ROOT }),
        qc.invalidateQueries({ queryKey: adminKeys.coverageGapsRoot() }),
      ]),
  });
}

export function useRejectDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminApi.reject(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: DRAFTS_ROOT }),
  });
}

/**
 * BR-90 / PRE-123: the skill merge queue.
 *
 * Every action invalidates the skills list as well as the queue: merging,
 * promoting and accepting all change what the taxonomy contains or what it
 * redirects, and a screen showing the old answer next to the new one is how an
 * admin merges the same row twice.
 */
export function useSkillReviewQueue() {
  return useQuery({
    queryKey: adminKeys.skillReviewQueue(),
    queryFn: () => adminApi.listSkillReviewQueue(),
  });
}

function useQueueMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.skillReviewQueue() });
      void qc.invalidateQueries({ queryKey: adminKeys.skillsAll() });
    },
  });
}

export function useMergeSkill() {
  return useQueueMutation(({ id, targetId }: { id: string; targetId: string }) =>
    adminApi.mergeSkill(id, targetId),
  );
}

export function usePromoteSkill() {
  return useQueueMutation(
    ({ id, ...input }: { id: string; title?: string; slug?: string; parentId?: string | null }) =>
      adminApi.promoteSkill(id, input),
  );
}

export function useAcceptSkill() {
  return useQueueMutation(({ id }: { id: string }) => adminApi.acceptSkill(id));
}

/**
 * PRE-289: launch readiness. A read plus the one write a person owns.
 *
 * The report is computed end to end — no part of the worklist is tickable, and
 * there is deliberately no mutation for "mark this done". The only thing this
 * hook can write is a waiver, which is not a fact about the bank but a decision
 * to ship without one.
 */
export function useLaunchReadiness() {
  return useQuery({
    queryKey: adminKeys.launchReadiness(),
    queryFn: () => adminApi.getLaunchReadiness(),
  });
}

export function useCreateLaunchWaiver() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LaunchWaiverInput) => adminApi.createLaunchWaiver(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.launchReadiness() });
    },
  });
}

export function useDeleteLaunchWaiver() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminApi.deleteLaunchWaiver(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.launchReadiness() });
    },
  });
}
